/// <amd-module name="Repos/Files/Components/ContentPreview" />
import * as React from "react";
import { IFileContentDetails } from "Repos/Common/Contracts/Editor";
import { getParentPath } from "Repos/Common/Util/File";
import { IFilesHubUrlTransformer, IGitItemContent } from "Repos/Files/Contracts";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IExternalContentLoadedEvent } from "VSS/Ext/ExternalContent";
import { ContributedContent, IContributedContentProps } from "VSS/Features/PlatformUI/ContributedContent";
import { PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { css, VssComponent } from "VSS/Platform/Layout";
import { combineUrlPaths, resolveRelativePath, Uri } from "VSS/Platform/Util/Url";
import { Card } from "VSSUI/Card";
import { Observer } from "VSSUI/Observer";
import { IContentPreviewContribution } from "../ContentPreview";

interface ILegacyContributionRenderer {
    renderContent: (content: string, options?: any) => void;
}

interface IContentPreviewProps {
    className?: string;
    contributionId: string;
    contribution: IContentPreviewContribution;
    getItemContent: () => IReadonlyObservableValue<IGitItemContent | undefined>;
    urlTransformer: IFilesHubUrlTransformer;
}

export class ContentPreview extends VssComponent<IContentPreviewProps> {
    private legacyRenderer: ILegacyContributionRenderer | null;
    private content = new ObservableValue<string>("");

    public render() {
        const { className, contribution, contributionId, getItemContent, urlTransformer } = this.props;

        return (
            <PerformanceScenarioContext.Consumer>
                {scenarioProps => {
                    const pendingWorkComplete = scenarioProps.registerPendingWork("load-preview")!;
                    return (
                        <Observer content={getItemContent()}>
                            {(contentProps: { content: IGitItemContent | undefined }) => {
                                let contentDetails: IFileContentDetails | undefined;
                                if (contentProps.content) {
                                    contentDetails = contentProps.content.details;
                                    if (contentProps.content.editContext) {
                                        contentDetails = {
                                            content: contentProps.content.editContext.getContent(),
                                            contentTruncated: contentProps.content.details.contentTruncated,
                                            metadata: contentProps.content.details.metadata,
                                            objectId: contentProps.content.details.objectId,
                                            resourceUri: contentProps.content.details.resourceUri
                                        };
                                    }
                                }

                                this.content.value = contentDetails ? contentDetails.content : "";

                                const contributedProps: IContributedContentProps = {
                                    componentProperties: {
                                        context: { content: this.content.value },
                                        linkTransformer: urlTransformer.transformLink,
                                        imageUrlTransformer: urlTransformer.transformImage,
                                        linkParse: urlTransformer.parseFilesHubUrl,
                                        onLoaded: this.onLoaded
                                    },
                                    contribution,
                                    contributionId
                                };

                                return (
                                    <Card className="flex-grow no-padding" contentProps={{ contentPadding: false }}>
                                        {contentDetails ? (
                                            <ContributedContent
                                                onDependenciesResolved={pendingWorkComplete && pendingWorkComplete.complete}
                                                className={css(className, "flex-grow")}
                                                {...contributedProps}
                                            />
                                        ) : (
                                            <div className="repos-file-editor-background flex-grow" />
                                        )}
                                    </Card>
                                );
                            }}
                        </Observer>
                    );
                }}
            </PerformanceScenarioContext.Consumer>
        );
    }

    public componentDidMount(): void {
        this.content.subscribe(this.onContentChanged);
    }

    public componentWillUnmount(): void {
        this.content.unsubscribe(this.onContentChanged);
        this.legacyRenderer = null;
    }

    private onContentChanged = (content: string): void => {
        this.renderLegacyContent(content);
    };

    private onLoaded = (event: IExternalContentLoadedEvent): void => {
        // This is to support legacy 3rd party extensions where they
        // register an object with renderContent method
        if (event.proxy) {
            event.proxy
                .getRemoteObjectProxy(this.props.contribution.registeredObjectId || "showRenderer")
                .then((renderer: ILegacyContributionRenderer) => {
                    this.legacyRenderer = renderer;
                    this.renderLegacyContent(this.content.value);
                })
                .catch(() => {
                    // It is ok legacy method is not found since
                    // the extension may be using the new SDK
                });
        }
    };

    private renderLegacyContent(content: string): void {
        if (this.legacyRenderer) {
            this.legacyRenderer.renderContent(content, {});
        }
    }
}

export class MarkdownFilesHubUrlTransformer implements IFilesHubUrlTransformer {
    constructor(
        private path: string,
        private isFolder: boolean,
        private isSelectedVersion: (versionString: string) => boolean,
        private getItemUrl: (path: string, anchor: string) => string,
        private getFileDownloadUrl: (path: string) => string
    ) {}

    public transformImage = (src: string): string => {
        const uri = new Uri(src);
        if (uri.scheme || uri.host) {
            // If it's absolute, preserve URI.
            return src;
        }

        return this.getFileDownloadUrl(this.getTargetPath(src));
    };

    public transformLink = (src: string): string => {
        try {
            const uri = new Uri(src);
            if (uri.scheme || uri.host) {
                // If it's absolute, preserve URI.
                return src;
            } else if (uri.path) {
                return this.getItemUrl(this.getTargetPath(uri.path), tryDecode(uri.hashString));
            } else if (uri.hashString) {
                return this.getItemUrl(this.path, tryDecode(uri.hashString));
            } else {
                return src;
            }
        } catch (e) {
            // protecting against user entering an invalid URI which would crash content preview
        }
        return src;
    };

    public parseFilesHubUrl = (href: string): { path: string; anchor: string } | undefined => {
        // If the link is a files-hub link, let's navigate to it without reloading the page
        const uri = new Uri(href);
        if (!uri.scheme || !uri.host) {
            const version = uri.getQueryParam("version");
            // If the URL is explicitly for a different version, we'll leave a hard refresh (unfrequent case).
            if (!version || this.isSelectedVersion(version)) {
                const currentPath = this.path;
                let path = uri.getQueryParam("path") || "";
                if (path === currentPath) {
                    // Don't navigate to the current path, as it would reload the item again (even inner-page navigation).
                    // Instead consider the path was not provided and use anchor only (if present).
                    path = "";
                }

                const anchor = uri.hashString || uri.getQueryParam("anchor") || "";

                if (path || anchor) {
                    return { path, anchor };
                }
            }
        }
    };

    private getTargetPath(path: string): string {
        if (path[0] === "/") {
            return path;
        }

        const root = this.path[0] === "$" ? "$/" : "/";

        const folderPath = this.isFolder ? this.path : getParentPath(this.path, root) || "";
        const targetPath = resolveRelativePath(combineUrlPaths(folderPath, path));

        // path may come either encoded or not, so we try to decode to prevent double encoding.
        return tryDecode(targetPath);
    }
}

function tryDecode(path: string): string {
    try {
        return decodeURIComponent(path);
    } catch {
        return path;
    }
}
